import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faPlus, faTrash, faEdit, faFileCsv,
  faCheckCircle, faExclamationTriangle, faSync, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_URL = `${API_CONFIG2.apiUrl}offres/api_offrerecrutement.php`;

// ============================================================
// DEXIE CACHE (24h + cache périmé)
// ============================================================
const db = new Dexie('OffresRecrutementCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000;

async function getCachedOffres(key) {
  const entry = await db.cache.get(key);
  if (entry && entry.timestamp && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.data;
  }
  return null;
}
async function setCachedOffres(key, data) {
  await db.cache.put({ key, data, timestamp: Date.now() });
}
async function getStaleOffres(key) {
  const entry = await db.cache.get(key);
  return entry ? entry.data : null;
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES
// ============================================================
const pendingFetches = new Map();

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const ManageOffresRecrutement = () => {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });

  const [source, setSource] = useState('cache');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingOffre, setEditingOffre] = useState(null);
  const [formData, setFormData] = useState({
    id: '', titre: '', service: '', description: '', datePublication: '', statut: 'Brouillon', nbPostes: 1
  });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);

  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const getCacheKey = (page, search) => `offres_${page}_${search}`;

  // ============================================================
  // FONCTION DE CHARGEMENT (avec déduplication, annulation, cache)
  // ============================================================
  const loadData = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = getCacheKey(page, search);
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Vérifier si une requête identique est en cours
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (result) {
          setOffres(result.data);
          setPagination(result.pagination);
          setSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache frais (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedOffres(cacheKey);
          if (cached) {
            setOffres(cached.data);
            setPagination(cached.pagination);
            setSource('cache');
            setLoading(false);
            setRefreshing(false);
            return cached;
          }
        }

        // 2. Requête réseau
         const uniqueParam = new Date().getTime();
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}&unique=${uniqueParam}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          // Essayer le cache périmé
          const stale = await getStaleOffres(cacheKey);
          if (stale) {
            setOffres(stale.data);
            setPagination(stale.pagination);
            setSource('cache (périmé)');
            setToast({ show: true, message: '429 – Utilisation du cache périmé', variant: 'warning' });
            setTimeout(() => setToast({ show: false }), 5000);
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          setToast({ show: true, message: `429 – Nouvel essai dans ${waitTime/1000}s`, variant: 'warning' });
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return loadData(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          // Fallback cache périmé en cas d'erreur réseau
          const stale = await getStaleOffres(cacheKey);
          if (stale) {
            setOffres(stale.data);
            setPagination(stale.pagination);
            setSource('cache (périmé)');
            setToast({ show: true, message: `Erreur réseau – Utilisation du cache`, variant: 'warning' });
            setTimeout(() => setToast({ show: false }), 5000);
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          throw new Error('Réponse JSON invalide');
        }

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          setOffres(data.data);
          setPagination(data.pagination);
          const sourceLabel = forceRefresh ? 'live (forcé)' : 'live';
          setSource(sourceLabel);
          await setCachedOffres(cacheKey, result);
          if (forceRefresh) {
            setToast({ show: true, message: 'Données actualisées', variant: 'success' });
            setTimeout(() => setToast({ show: false }), 3000);
          }
          setLoading(false);
          setRefreshing(false);
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleOffres(cacheKey);
        if (stale) {
          setOffres(stale.data);
          setPagination(stale.pagination);
          setSource('cache (périmé)');
          setToast({ show: true, message: `Erreur – Utilisation du cache : ${err.message}`, variant: 'warning' });
          setTimeout(() => setToast({ show: false }), 5000);
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        setToast({ show: true, message: err.message || 'Erreur de chargement', variant: 'danger' });
        setTimeout(() => setToast({ show: false }), 5000);
        setLoading(false);
        setRefreshing(false);
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit]);

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadData(1, val, false);
    }, 400);
  };

  // ============================================================
  // INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    loadData(1, '', false);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // ============================================================
  // RAFRAÎCHISSEMENT FORCÉ
  // ============================================================
  const handleRefresh = () => {
    if (!loading && !refreshing) {
      loadData(pagination.currentPage, searchTerm, true);
    }
  };

  // ============================================================
  // FONCTIONS CRUD (avec rechargement forcé)
  // ============================================================
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const method = editingOffre ? 'PUT' : 'POST';
    const action = editingOffre ? 'update' : 'create';
    try {
      const res = await fetch(`${API_URL}?action=${action}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch(e) { throw new Error('Réponse non JSON'); }
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowModal(false);
        loadData(pagination.currentPage, searchTerm, true);
      } else {
        setToast({ show: true, message: data.message || 'Erreur', variant: 'danger' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Erreur serveur ou réponse invalide', variant: 'danger' });
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}?action=delete&id=${formData.id}`, { method: 'DELETE' });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch(e) { throw new Error('Réponse non JSON'); }
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowDeleteModal(false);
        loadData(pagination.currentPage, searchTerm, true);
      } else {
        setToast({ show: true, message: data.message || 'Erreur', variant: 'danger' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Erreur serveur ou réponse invalide', variant: 'danger' });
    }
  };

  const handleImportCSV = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setToast({ show: true, message: 'Veuillez sélectionner un fichier CSV', variant: 'danger' });
      return;
    }
    const formDataFile = new FormData();
    formDataFile.append('file', selectedFile);
    try {
      const res = await fetch(`${API_URL}?action=import`, { method: 'POST', body: formDataFile });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch(e) { throw new Error('Réponse non JSON'); }
      if (data.status === 'success') {
        setToast({ show: true, message: data.message, variant: 'success' });
        setShowImportModal(false);
        setSelectedFile(null);
        loadData(1, searchTerm, true);
      } else {
        setToast({ show: true, message: data.message || 'Erreur', variant: 'danger' });
      }
    } catch (err) {
      setToast({ show: true, message: 'Erreur lors de l\'import', variant: 'danger' });
    }
  };

  const openEditModal = (item) => {
    setEditingOffre(item);
    setFormData({ ...item, nbPostes: parseInt(item.nbPostes) });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingOffre(null);
    setFormData({ id: '', titre: '', service: '', description: '', datePublication: '', statut: 'Brouillon', nbPostes: 1 });
    setShowModal(true);
  };

  // ============================================================
  // UTILITAIRES D'AFFICHAGE
  // ============================================================
  const getStatusColor = (statut) => {
    switch(statut) {
      case 'Publiée': return 'var(--accent-blue)';
      case 'Brouillon': return '#f59e0b';
      case 'Clôturée': return '#6b7280';
      default: return 'var(--text-secondary)';
    }
  };
  const getStatusBg = (statut) => {
    switch(statut) {
      case 'Publiée': return 'rgba(59,130,246,0.15)';
      case 'Brouillon': return 'rgba(245,158,11,0.15)';
      case 'Clôturée': return 'rgba(107,114,128,0.15)';
      default: return 'rgba(255,255,255,0.04)';
    }
  };

  // ============================================================
  // RENDU
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           STYLES AMÉLIORÉS – ManageOffresRecrutement (avec cache + mobile)
           ============================================================ */
        .offres-wrapper { padding: 0; }
        .offres-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          margin-bottom: 24px;
        }
        .offres-header h1 {
          font-size: clamp(1.4rem, 4vw, 2rem);
          font-weight: 700;
          background: linear-gradient(135deg, #e0e7ff, #94a3b8);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
        }
        .offres-header .sub {
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 1.2vw, 0.95rem);
          margin-top: 4px;
        }
        .offres-actions {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items: center;
        }
        .badge-source {
          font-size: clamp(0.6rem, 1vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }
        .btn-glass {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 40px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          font-weight: 500;
          font-size: clamp(0.7rem, 1vw, 0.9rem);
          transition: 0.2s;
          cursor: pointer;
          text-decoration: none;
          backdrop-filter: blur(4px);
          min-height: 44px;
          touch-action: manipulation;
        }
        .btn-glass:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          border-color: rgba(255,255,255,0.12);
          color: #fff;
        }
        .btn-glass:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .btn-glass.primary {
          background: rgba(59,130,246,0.15);
          border-color: rgba(59,130,246,0.2);
          color: var(--accent-blue);
        }
        .btn-glass.primary:hover:not(:disabled) {
          background: rgba(59,130,246,0.25);
          border-color: rgba(59,130,246,0.3);
        }
        .btn-glass.danger {
          background: rgba(239,68,68,0.12);
          border-color: rgba(239,68,68,0.15);
          color: #f87171;
        }
        .btn-glass.danger:hover:not(:disabled) {
          background: rgba(239,68,68,0.2);
          border-color: rgba(239,68,68,0.25);
        }
        .btn-glass.icon-only {
          padding: 8px 10px;
          min-width: 40px;
          justify-content: center;
        }
        .spinner-small {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.1);
          border-top-color: var(--accent-blue);
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        .glass-card-offres {
          background: var(--card-bg);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          padding: clamp(0.8rem, 1.5vw, 1.2rem);
          margin-bottom: 24px;
          transition: border-color 0.2s;
        }
        .glass-card-offres:hover {
          border-color: rgba(59,130,246,0.25);
        }
        .search-wrapper-offres {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 40px;
          padding: 0 16px;
          transition: 0.3s;
          flex: 1;
          max-width: 400px;
          min-height: 44px;
        }
        .search-wrapper-offres:focus-within {
          border-color: var(--accent-blue);
          background: rgba(255,255,255,0.06);
        }
        .search-wrapper-offres svg {
          color: var(--text-secondary);
          margin-right: 12px;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
        }
        .search-wrapper-offres input {
          background: transparent;
          border: none;
          padding: 10px 0;
          color: var(--text-primary);
          outline: none;
          width: 100%;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper-offres input::placeholder {
          color: var(--text-secondary);
        }

        .table-offres {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .table-offres thead th {
          padding: 12px 12px;
          text-align: left;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .table-offres tbody td {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
          color: var(--text-primary);
          vertical-align: middle;
        }
        .table-offres tbody tr:hover {
          background: rgba(255,255,255,0.02);
        }
        .table-offres .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          font-weight: 500;
          background: var(--status-bg, rgba(255,255,255,0.04));
          color: var(--status-color, var(--text-secondary));
          border: 1px solid rgba(255,255,255,0.04);
        }
        .table-offres .actions-cell {
          display: flex;
          gap: 6px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        .table-offres .id-cell {
          font-weight: 600;
          color: var(--text-secondary);
        }
        .pagination-offres {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          padding: 12px 16px 0;
          border-top: 1px solid rgba(255,255,255,0.04);
          margin-top: 8px;
        }
        .pagination-offres .info {
          color: var(--text-secondary);
          font-size: clamp(0.7rem, 1vw, 0.85rem);
        }
        .pagination-offres .controls {
          display: flex;
          gap: 6px;
        }

        /* Modales */
        .modal-glass .modal-content {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59,130,246,0.1);
          border-radius: 20px;
          color: var(--text-primary);
        }
        .modal-glass .modal-header {
          border-bottom: 1px solid rgba(255,255,255,0.04);
          padding: 16px 24px;
        }
        .modal-glass .modal-header .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .modal-glass .modal-body {
          padding: 24px;
        }
        .modal-glass .modal-footer {
          border-top: 1px solid rgba(255,255,255,0.04);
          padding: 16px 24px;
        }
        .modal-glass .form-label {
          color: var(--text-secondary);
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          margin-bottom: 4px;
        }
        .modal-glass .form-control, .modal-glass .form-select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 12px;
          padding: 10px 14px;
          color: var(--text-primary);
          transition: 0.2s;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .modal-glass .form-control:focus, .modal-glass .form-select:focus {
          border-color: var(--accent-blue);
          background: rgba(255,255,255,0.06);
          box-shadow: none;
        }
        .modal-glass .form-control::placeholder {
          color: var(--text-secondary);
        }
        .modal-glass .form-select option {
          background: #0b1428;
        }
        .modal-glass .btn-outline-secondary {
          color: var(--text-secondary);
          border-color: rgba(255,255,255,0.06);
        }
        .modal-glass .btn-outline-secondary:hover {
          background: rgba(255,255,255,0.04);
          color: #fff;
        }

        /* Toast */
        .toast-glass {
          background: rgba(10, 20, 45, 0.95);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(59,130,246,0.1);
          border-radius: 16px;
          color: var(--text-primary);
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
          padding: 14px 20px;
          min-width: clamp(200px, 30vw, 280px);
        }
        .toast-glass .toast-close {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          font-size: 1.2rem;
          cursor: pointer;
          transition: 0.2s;
        }
        .toast-glass .toast-close:hover {
          color: #fff;
        }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .offres-header {
            flex-direction: column;
            align-items: stretch;
          }
          .offres-actions {
            justify-content: stretch;
            flex-wrap: wrap;
          }
          .offres-actions .btn-glass {
            flex: 1 0 auto;
            justify-content: center;
            min-height: 40px;
          }
          .search-wrapper-offres {
            max-width: 100%;
          }
          .table-offres {
            font-size: 0.75rem;
          }
          .table-offres thead th,
          .table-offres tbody td {
            padding: 6px 8px;
          }
          .table-offres .actions-cell .btn-glass {
            padding: 4px 8px;
            min-width: 32px;
          }
          .pagination-offres {
            flex-direction: column;
            align-items: center;
          }
          .modal-glass .modal-body {
            padding: 16px;
          }
        }
        @media (max-width: 480px) {
          .btn-glass {
            font-size: 0.7rem;
            padding: 6px 12px;
            min-height: 36px;
          }
          .btn-glass.icon-only {
            padding: 4px 6px;
            min-width: 30px;
          }
          .table-offres {
            font-size: 0.7rem;
          }
        }
      `}</style>

      <div className="offres-wrapper">
        {/* HEADER */}
        <div className="offres-header">
          <div>
            <h1>📋 Gestion des offres</h1>
            <div className="sub">Publiez, modifiez et gérez vos offres d'emploi.</div>
          </div>
          <div className="offres-actions">
            <span className={`badge-source ${source.includes('live') ? 'live' : source.includes('périmé') ? 'warning' : ''}`}>
              {source === 'cache' ? '📦 Cache' :
               source === 'cache (périmé)' ? '⚠️ Cache' :
               source === 'cache (partagé)' ? '🔄 Partage' :
               source === 'live' ? '🔄 Live' :
               source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="btn-glass" onClick={handleRefresh} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSync} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="btn-glass" onClick={() => setShowImportModal(true)}>
              <FontAwesomeIcon icon={faFileCsv} /> Importer CSV
            </button>
            <button className="btn-glass primary" onClick={openCreateModal}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter
            </button>
          </div>
        </div>

        {/* SEARCH */}
        <div className="glass-card-offres">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="search-wrapper-offres">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Rechercher (titre, service, statut...)"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="ms-auto text-secondary small">
              {pagination.totalRecords} enregistrements
            </div>
          </div>
        </div>

        {/* TABLE */}
        <div className="glass-card-offres p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="table-offres">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Titre</th>
                  <th>Service</th>
                  <th>Postes</th>
                  <th>Date publication</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="7" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : offres.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-5 text-secondary">Aucune offre trouvée.</td></tr>
                ) : (
                  offres.map(item => (
                    <tr key={item.id}>
                      <td><span className="id-cell">#{item.id}</span></td>
                      <td><strong>{item.titre}</strong></td>
                      <td><span className="text-secondary">{item.service}</span></td>
                      <td><span className="text-secondary">{item.nbPostes}</span></td>
                      <td><span className="text-secondary">{item.datePublication}</span></td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            '--status-bg': getStatusBg(item.statut),
                            '--status-color': getStatusColor(item.statut),
                          }}
                        >
                          {item.statut}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="actions-cell">
                          <button className="btn-glass icon-only" onClick={() => openEditModal(item)} title="Modifier">
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button className="btn-glass icon-only danger" onClick={() => { setFormData(item); setShowDeleteModal(true); }} title="Supprimer">
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="pagination-offres">
            <span className="info">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="controls">
              <button
                className="btn-glass icon-only"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => loadData(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="btn-glass icon-only"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => loadData(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>

        {/* TOAST */}
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          {toast.show && (
            <div className="toast-glass d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-primary' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button className="toast-close ms-auto" onClick={() => setToast({ ...toast, show: false })}>
                ✕
              </button>
            </div>
          )}
        </div>

        {/* MODALES (inchangées) */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered className="modal-glass">
          <Form onSubmit={handleFormSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon icon={faEdit} className="text-primary me-2" />
                {editingOffre ? 'Modifier' : 'Ajouter'} une offre
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="row g-3">
                <div className="col-12">
                  <Form.Label className="form-label">Titre</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.titre}
                    onChange={(e) => setFormData({...formData, titre: e.target.value})}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">Service</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">Nombre de postes</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={formData.nbPostes}
                    onChange={(e) => setFormData({...formData, nbPostes: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div className="col-12">
                  <Form.Label className="form-label">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">Date de publication</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.datePublication}
                    onChange={(e) => setFormData({...formData, datePublication: e.target.value})}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <Form.Label className="form-label">Statut</Form.Label>
                  <Form.Select
                    value={formData.statut}
                    onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  >
                    <option value="Brouillon">Brouillon</option>
                    <option value="Publiée">Publiée</option>
                    <option value="Clôturée">Clôturée</option>
                  </Form.Select>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Annuler</Button>
              <Button variant="primary" type="submit" style={{ background: 'var(--accent-blue)', border: 'none' }}>
                Enregistrer
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="modal-glass">
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              Confirmation
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Êtes-vous sûr de vouloir supprimer l'offre <strong>"{formData.titre}"</strong> ?
            <br /><span className="text-secondary small">Cette action est irréversible.</span>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
            <Button variant="danger" onClick={handleDelete}>Confirmer la suppression</Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered className="modal-glass">
          <Form onSubmit={handleImportCSV}>
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon icon={faFileCsv} className="text-info me-2" />
                Importer des données CSV
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="text-secondary small">
                Le fichier doit avoir les colonnes dans cet ordre :<br />
                <code>titre, service, description, datePublication, statut, nbPostes</code>
              </p>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                required
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShowImportModal(false)}>Annuler</Button>
              <Button variant="primary" type="submit" style={{ background: 'var(--accent-blue)', border: 'none' }}>
                Importer
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default ManageOffresRecrutement;